import {
  type AvailableGraphEntities,
  type GraphEdgeSettings,
  type TopologyEdgeSettings,
  type TopologyGraphPreset,
  isGroupEdgeSettings,
  isTopologyEdgeSettings,
} from '@ipfabric/utils';
import { isTopologyGraphMetadata } from 'components/Graph/GraphState';
import { loadGraph } from 'components/Graph/commands/loadGraph';
import { setAppliedPreset } from 'components/Graph/commands/settings/setAppliedPreset';
import { clearSettings } from 'components/Graph/helpers/clearSettings';
import type { GraphCommandFactory } from 'components/Graph/model/GraphCommandContext';
import { pipe } from 'fp-ts/lib/function';
import deepEqual from 'react-fast-compare';
import { omit } from 'utils/object/omit';
import { clearSharedView } from '../helpers/clearSharedView';

export interface ApplyTopologySettingsCommand {
  fetchDataDisabled?: boolean;
}

const getTolopologyEdgeSettingsForDiff = (edgeSettings: TopologyEdgeSettings) =>
  omit(['labels', 'style', 'id'], edgeSettings);

const isEdgeSettingsUsed =
  (availableEntities: AvailableGraphEntities) =>
  (edgeSettings: GraphEdgeSettings) =>
    (isGroupEdgeSettings(edgeSettings) &&
      edgeSettings.children.some((child) =>
        availableEntities.edgeSettingsIds.includes(child.id),
      )) ||
    (isTopologyEdgeSettings(edgeSettings) &&
      availableEntities.edgeSettingsIds.includes(edgeSettings.id));

/**
 * Filter out properties updated locally & edge settings not present in context
 * so we can avoid unnecessary graph load
 */
const getPresetForDiff = (
  preset: TopologyGraphPreset,
  availableEntities: AvailableGraphEntities,
) => ({
  edges: preset.edges
    .filter(isEdgeSettingsUsed(availableEntities))
    .map((edgeSettings) => {
      if (isTopologyEdgeSettings(edgeSettings)) {
        return availableEntities.edgeSettingsIds.includes(edgeSettings.id)
          ? getTolopologyEdgeSettingsForDiff(edgeSettings)
          : undefined;
      }
      if (isGroupEdgeSettings(edgeSettings)) {
        const { children, label, name, style, ...otherSettings } = edgeSettings;
        return {
          ...omit(['id'], otherSettings),
          children: children
            .filter(isTopologyEdgeSettings)
            .filter((topologyEdgeSettings) =>
              availableEntities.edgeSettingsIds.includes(
                topologyEdgeSettings.id,
              ),
            )
            .map((topologyEdgeSettings) =>
              getTolopologyEdgeSettingsForDiff(topologyEdgeSettings),
            ),
        };
      }
      return null;
    }),
  hiddenDeviceTypes: preset.hiddenDeviceTypes.filter((deviceType) =>
    availableEntities.deviceTypes.includes(deviceType),
  ),
});

/**
 * Apply settings = update site using current value of layoutSettings.presetDraft
 */
export const applyTopologySettings: GraphCommandFactory<
  ApplyTopologySettingsCommand | undefined
> = (cmdContext, dispatch) => {
  return async (command) => {
    cmdContext.updateState(clearSharedView);

    const {
      layoutSettings: { presetDraft, preset },
      graphMetadata,
    } = cmdContext.getState();

    pipe(
      cmdContext.getState().layoutSettings.presetDraft,
      clearSettings,
      (presetToApply) => dispatch(setAppliedPreset, { preset: presetToApply }),
    );

    if (!isTopologyGraphMetadata(graphMetadata)) {
      return;
    }

    const { availableEntities } = graphMetadata;

    const presetDraftForDiff = getPresetForDiff(presetDraft, availableEntities);
    const presetForDiff = getPresetForDiff(preset, availableEntities);

    const currentGraphNotAffected = deepEqual(
      presetDraftForDiff,
      presetForDiff,
    );

    if (!command?.fetchDataDisabled && !currentGraphNotAffected) {
      await dispatch(loadGraph, { overlay: 'keep' });
    }
  };
};
